import React from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  hintText?: string;
  errorText?: string;
  maxHeight?: number;
}

const DEFAULT_HEIGHT = 106;
const DEFAULT_MAX_HEIGHT = 240;

/** Multiline text input with label, hint, and error states. */
export const TextArea = ({
  label,
  value,
  onChange,
  hintText,
  errorText,
  maxHeight = DEFAULT_MAX_HEIGHT,
  disabled,
  ...rest
}: TextAreaProps): JSX.Element => {
  const hasError = Boolean(errorText);

  return (
    <Wrapper>
      {label && <Label>{label}</Label>}
      <Field
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        $hasError={hasError}
        $maxHeight={maxHeight}
        {...rest}
      />
      {errorText ? (
        <HelperText $tone="error">{errorText}</HelperText>
      ) : (
        hintText && <HelperText $tone="hint">{hintText}</HelperText>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue2};
`;

const Field = styled.textarea<{
  $hasError: boolean;
  $maxHeight: number;
}>`
  ${TypographyV2.b2};
  width: 100%;
  min-height: ${DEFAULT_HEIGHT}px;
  max-height: ${(props) => props.$maxHeight}px;
  padding: 16px 24px;
  border-radius: 16px;
  border: 1px solid
    ${(props) => (props.$hasError ? ColorV2.NegativeRed : ColorV2.Blue5)};
  color: ${ColorV2.Blue1};
  background-color: ${ColorV2.White};
  resize: vertical;
  overflow: auto;

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.$hasError ? ColorV2.NegativeRed : ColorV2.Blue4};
  }

  &:disabled {
    background-color: ${ColorV2.Blue7};
    color: ${ColorV2.Blue5};
    cursor: not-allowed;
  }
`;

const HelperText = styled.span<{ $tone: "hint" | "error" }>`
  ${TypographyV2.s1};
  color: ${(props) =>
    props.$tone === "error" ? ColorV2.NegativeRed : ColorV2.Blue4};
`;
